import type { ReactNode } from 'react';
import { useContext, useState, useMemo, createContext } from 'react';
import { v4 as uuid } from 'uuid';

import type { Message } from './Messages.types';

/** Default time in milliseconds before the message will be automatically dismissed */
const AUTO_DISMISS_MS_DEFAULT = 5000;

interface Context {
    messages: Record<string, Message[]>;
    addMessage: (message: Message, config?: Config) => void;
    removeMessageById: (messageId: string, config?: Config) => void;
    clearMessages: (config?: Config) => void;
}

export const MessagesContext = createContext<Context>({
    messages: {},
    addMessage: () => {},
    removeMessageById: () => {},
    clearMessages: () => {},
});

export const DEFAULT_QUEUE = 'global';

interface Config {
    /** The message "queue" to use, default: "global". */
    queue: string;
}

interface MessagesProviderProps {
    children: ReactNode;
}

export const useMessagesContext = () => useContext(MessagesContext);

const getConfigWithDefaults = (config?: Config): Config => ({
    queue: DEFAULT_QUEUE,
    ...(config ?? {}),
});

/**
 * This component holds the messages state for the MessagesContext, and makes it available to its children
 * through the context provider.
 *
 * ### Example
 * ```tsx
 * <MessagesProvider>{children}<MessagesProvider/>
 * ```
 */
export function MessagesProvider({ children }: MessagesProviderProps) {
    const [messages, setMessages] = useState<Record<string, Message[]>>({});

    const removeMessageById = (messageId: string, config?: Config) => {
        const { queue } = getConfigWithDefaults(config);
        const messagesForQueue = messages[queue] ?? [];

        const message = messagesForQueue.find((item) => item.id === messageId);
        if (message?.timeoutId) {
            clearTimeout(message.timeoutId);
        }

        setMessages((currentMessages) => ({
            ...currentMessages,
            [queue]: (currentMessages[queue] ?? []).filter(
                (currentMessage) => currentMessage.id !== messageId,
            ),
        }));
    };

    const addMessage = (message: Message, config?: Config) => {
        const { queue } = getConfigWithDefaults(config);
        const messageWithDefaults = {
            ...message,
            id: uuid(),
            dismissible: message.dismissible ?? true,
        };

        // Automatically dismiss messages after a certain amount of time (unless persistent)
        let timeoutId: number | undefined;
        if (!messageWithDefaults.persistent) {
            const timeout = setTimeout(() => {
                removeMessageById(messageWithDefaults.id, config);

                if (messageWithDefaults.onClose) {
                    messageWithDefaults.onClose();
                }
            }, messageWithDefaults.autoDismissMs ?? AUTO_DISMISS_MS_DEFAULT);
            timeoutId = Number(timeout);
        }

        setMessages((currentMessages) => ({
            ...currentMessages,
            [queue]: [...(currentMessages[queue] ?? []), { ...messageWithDefaults, timeoutId }],
        }));
    };

    const clearMessages = (config?: Config) => {
        const { queue } = getConfigWithDefaults(config);

        (messages[queue] ?? []).map(({ id }) => removeMessageById(id ?? ''));
    };

    const value = useMemo(
        () => ({ messages, addMessage, removeMessageById, clearMessages }),
        [messages],
    );

    return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
}
